import { first } from 'lodash';
import { useMemo } from 'react';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { EntityStage } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { CashflowDrawerFormShape } from '../CashflowDrawer.types';
import {
  HouseholdCashflowEntityOptionsQueryHookResult,
  useHouseholdCashflowEntityOptionsQuery,
} from '../graphql/HouseholdCashflowEntityOptions.generated';

export function useEntityTargets(
  householdId: string | null
): [
  TypeaheadSelectInputOption<CashflowDrawerFormShape['targetId']>[],
  HouseholdCashflowEntityOptionsQueryHookResult,
] {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const queryData = useHouseholdCashflowEntityOptionsQuery({
    variables: {
      where: { id: householdId },
    },
    onError: (error) => {
      showFeedback('Could not fetch transfer targets.');
      reportError('COuld not fetch transfer targets', error);
    },
    skip: !householdId,
  });

  const household = first(getNodes(queryData.data?.households));

  const options = useMemo<
    TypeaheadSelectInputOption<CashflowDrawerFormShape['targetId']>[]
  >(() => {
    if (!household) {
      return [];
    }
    const { entities } = household || {};

    return getNodes(entities)
      .filter((entity) => entity.stage !== EntityStage.PreCreated)
      .map<TypeaheadSelectInputOption<CashflowDrawerFormShape['targetId']>>(
        ({ id, subtype }) => ({
          value: id,
          display: subtype?.displayName,
          groupName: 'Entities',
        })
      );
  }, [household]);

  return [options, queryData];
}
